import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    IconButton,
    Typography,
    Spinner,
    Alert,
    Chip,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from '@material-tailwind/react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import ChargeTypeDialog from '../../masterData/ChargeTypeDialog';
import { rootUrl } from '../../../config/config';

export default function ChargeTypesPage() {
    const { user } = useAuth();
    const [chargeTypes, setChargeTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadChargeTypes();
    }, []);

    const loadChargeTypes = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(`${rootUrl}/api/job-card-charge-types`, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });

            setChargeTypes(response.data?.data || []);
        } catch (err) {
            console.error('Error loading charge types:', err);
            setError(err.response?.data?.message || 'Failed to load charge types');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setSelectedType(null);
        setDialogOpen(true);
    };

    const handleEdit = (type) => {
        setSelectedType(type);
        setDialogOpen(true);
    };

    const handleDeleteClick = (type) => {
        setTypeToDelete(type);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!typeToDelete) return;

        try {
            setDeleteLoading(true);

            const response = await axios.delete(
                `${rootUrl}/api/job-card-charge-types/${typeToDelete.id}`,
                {
                    headers: { Authorization: `Bearer ${user?.token}` }
                }
            );

            if (response.data.success) {
                setDeleteDialogOpen(false);
                setTypeToDelete(null);
                loadChargeTypes();
            } else {
                setError(response.data.message || 'Failed to delete charge type');
            }
        } catch (err) {
            console.error('Error deleting charge type:', err);
            setError(err.response?.data?.message || 'Failed to delete charge type');
        } finally {
            setDeleteLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
                <Typography variant="h4" className="text-gray-900">
                    Job Card Charge Types
                </Typography>
                <Button
                    color="blue"
                    className="flex items-center gap-2"
                    onClick={handleAdd}
                >
                    <PlusIcon className="w-5 h-5" />
                    Add Charge Type
                </Button>
            </div>

            {error && <Alert color="red" className="mb-4">{error}</Alert>}

            {chargeTypes.length > 0 ? (
                <Card className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-4 text-sm font-semibold text-gray-900">Code</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Name</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Description</th>
                                <th className="p-4 text-sm font-semibold text-gray-900 text-right">Default Rate</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Status</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chargeTypes.map((type, index) => (
                                <tr key={type.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="p-4 text-sm font-semibold text-gray-900">{type.code}</td>
                                    <td className="p-4 text-sm text-gray-900 font-semibold">{type.name}</td>
                                    <td className="p-4 text-sm text-gray-700">{type.description || '-'}</td>
                                    <td className="p-4 text-sm text-gray-900 text-right">
                                        {type.default_rate ? `${new Intl.NumberFormat('en-KE', {
                                            style: 'currency',
                                            currency: 'KES'
                                        }).format(type.default_rate)}` : '-'}
                                    </td>
                                    <td className="p-4 text-sm">
                                        {type.is_active ? (
                                            <Chip value="Active" color="green" variant="outlined" size="sm" />
                                        ) : (
                                            <Chip value="Inactive" color="red" variant="outlined" size="sm" />
                                        )}
                                    </td>
                                    <td className="p-4 text-sm">
                                        <div className="flex gap-2">
                                            <IconButton
                                                size="sm"
                                                variant="text"
                                                color="blue"
                                                onClick={() => handleEdit(type)}
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="text"
                                                color="red"
                                                onClick={() => handleDeleteClick(type)}
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </IconButton>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            ) : (
                <Card className="p-8 text-center">
                    <Typography className="text-gray-500">
                        No charge types found
                    </Typography>
                </Card>
            )}

            {/* Type Dialog */}
            <ChargeTypeDialog
                open={dialogOpen}
                type={selectedType}
                onSave={loadChargeTypes}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectedType(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} handler={setDeleteDialogOpen}>
                <DialogHeader>Confirm Delete</DialogHeader>
                <DialogBody>
                    Are you sure you want to delete the charge type "{typeToDelete?.name}"? This action cannot be undone.
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        color="gray"
                        onClick={() => setDeleteDialogOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        onClick={handleConfirmDelete}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}
